import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function clamp(min: number, max: number, v: number): number {
  return Math.max(min, Math.min(max, v));
}

export function genId(): string {
  return `r${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
  }
  return sorted[mid] ?? 0;
}

export const DEFAULT_CLIP_FPS = 12;
export const MIN_CLIP_FPS = 1;
export const MAX_CLIP_FPS = 60;

export function clampClipFps(n: number | undefined): number {
  return clamp(MIN_CLIP_FPS, MAX_CLIP_FPS, Math.round(n ?? DEFAULT_CLIP_FPS));
}

/**
 * Copy a rectangular region of an ImageData into a new ImageData.
 * Row-wise `set/subarray` so it benefits from the engine's typed-array fast path
 * — much faster than per-pixel loops for large rects.
 */
export function copyImageDataRect(
  src: ImageData,
  rect: { x: number; y: number; w: number; h: number },
): ImageData {
  const out = new ImageData(rect.w, rect.h);
  const rowBytes = rect.w * 4;
  for (let row = 0; row < rect.h; row++) {
    const srcStart = ((rect.y + row) * src.width + rect.x) * 4;
    out.data.set(src.data.subarray(srcStart, srcStart + rowBytes), row * rowBytes);
  }
  return out;
}
